/**
 * UserSearchPanel — debounced search, filters overlay, age range,
 * include-deleted toggle, a scrollable list of UserRow items, and a pagination
 * footer.
 *
 * Server-driven: the panel owns the filter *UI* state and reports it upward via
 * `onFiltersChange`; the parent turns that into a backend query (see
 * userFilterMapping.buildUsersQuery), fetches the matching page, and passes the
 * rows back through `users` plus the current window through `pagination`. The
 * panel does NOT filter or paginate the array itself.
 *
 * Also exports: FilterChip, UserRow
 *
 * Ported from lib/shell.jsx (claude_design project 5c0836b6-…).
 */

import React, { useState, useEffect } from 'react';
import { Button } from 'src/ui/Button';
import { SearchInput } from 'src/ui/SearchInput';
import { Switch } from 'src/ui/Switch';
import { Checkbox } from 'src/ui/Checkbox';
import { Input } from 'src/ui/Input';
import { Icon } from 'src/ui/Icon';
import { Avatar } from 'src/ui/Avatar';
import { GenderIcon } from 'src/ui/Avatar';
import { Badge } from 'src/ui/Badge';
import { UserTypeBadge } from 'src/ui/Badge';
import { Dropdown } from 'src/ui/Dropdown';
import { MenuItem } from 'src/ui/Dropdown';
import { EmptyState } from 'src/ui/EmptyState';
import { Spinner } from 'src/ui/Spinner';
import type { PanelUser } from './types';
import type { FilterGroup } from './userFiltersConfig';
import { defaultUserFilterGroups } from './userFiltersConfig';

// ============================================================
// FilterChip
// ============================================================

export interface FilterChipProps {
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

export function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      className={'fchip' + (active ? ' active' : '')}
      onClick={onClick}
    >
      <span className="fchip-check">
        <Icon name="check" size={13} strokeWidth={3} />
      </span>
      {children}
    </button>
  );
}

// ============================================================
// UserRow
// ============================================================

export interface MenuAction {
  label: string;
  icon?: string;
  danger?: boolean;
  onClick: () => void;
}

export interface UserRowProps {
  user: PanelUser;
  selected?: boolean;
  onClick?: () => void;
  /** Optional action renderer — receives the user and returns a React node */
  action?: (user: PanelUser) => React.ReactNode;
  compact?: boolean;
}

export function UserRow({
  user,
  selected,
  onClick,
  action,
  compact,
}: UserRowProps) {
  const deleted = !!user.deleted;
  return (
    <div
      role="button"
      tabIndex={0}
      className={'list-row' + (selected ? ' selected' : '')}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.();
      }}
      style={deleted ? { opacity: 0.78 } : undefined}
    >
      {/* Avatar + online dot */}
      <div
        style={{
          position: 'relative',
          filter: deleted ? 'grayscale(0.7)' : undefined,
        }}
      >
        <Avatar
          name={user.name}
          src={deleted ? undefined : user.image}
          gender={user.gender as any}
          ring={!deleted}
          hue={user.hue}
          size={compact ? 32 : 38}
        />
        {user.online && !deleted && (
          <span
            style={{
              position: 'absolute',
              right: -1,
              bottom: -1,
              width: 10,
              height: 10,
              borderRadius: 99,
              background: 'var(--green-solid)',
              border: '2px solid var(--bg-canvas)',
            }}
          />
        )}
      </div>

      {/* Name + badges + sub-line */}
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row gap-2">
          <span
            style={{
              fontWeight: 500,
              fontSize: 'var(--fs-sm)',
              color: deleted ? 'var(--text-muted)' : undefined,
            }}
            className="truncate"
          >
            {user.name}
          </span>
          <GenderIcon gender={user.gender} size={13} />
          <UserTypeBadge type={user.type} size="sm" />
          {deleted && (
            <Badge color="gray" size="sm">
              Deleted
            </Badge>
          )}
        </div>
        <div
          className="text-muted truncate"
          style={{ fontSize: 'var(--fs-xs)' }}
        >
          {deleted
            ? 'Deleted ' + (user.deletedAt ?? '')
            : [user.age, user.loc].filter(Boolean).join(' · ')}
        </div>
      </div>

      {action ? action(user) : null}
    </div>
  );
}

// ============================================================
// UserSearchPanel
// ============================================================

/** Filter UI state reported to the parent so it can build a backend query. */
export interface UserPanelFilters {
  /** Free-text search (matched against email + profile name on the server). */
  search: string;
  /** Selected option values per filter group key, e.g. { gender: ['female'] }. */
  selections: Record<string, string[]>;
  ageMin: string;
  ageMax: string;
  includeDeleted: boolean;
}

/** Current pagination window, owned by the parent. */
export interface UserPanelPagination {
  offset: number;
  limit: number;
  /** Whether the server reported more rows beyond this page. */
  hasMore: boolean;
  /** True total matching the active filters (ignores limit/offset). */
  total?: number;
  onOffsetChange: (offset: number) => void;
}

export interface UserSearchPanelProps {
  users: PanelUser[];
  /** Highlighted row in single-select mode. */
  selectedId?: string;
  /**
   * Multi-select mode: renders a checkbox per row and highlights every id in
   * `selectedIds`. `onSelect` is called with the toggled id.
   */
  multiSelect?: boolean;
  /** Selected ids for multi-select highlighting. */
  selectedIds?: string[];
  onSelect: (id: string) => void;
  /** Optional action renderer passed through to each UserRow */
  rowAction?: (user: PanelUser) => React.ReactNode;
  /** Panel title; defaults to "Users" */
  title?: string;
  /**
   * True total from the server (counts all matching rows, not just the loaded
   * page). Shown in the count pill. Falls back to the loaded length.
   */
  totalCount?: number;
  /** Explicit height for the panel container */
  height?: number | string;
  /** Show the "Include deleted" toggle */
  enableDeleted?: boolean;
  /** Overflow menu items for the panel header */
  menu?: MenuAction[];
  /**
   * Filter group definitions.  Defaults to defaultUserFilterGroups.
   * Each group: { key, label, options: [value, label][] }
   */
  filterGroups?: FilterGroup[];
  /**
   * Called whenever the filter UI state changes (search is already debounced by
   * SearchInput). The parent maps this to a backend query and refetches.
   */
  onFiltersChange?: (filters: UserPanelFilters) => void;
  /** Pagination window + navigation callback. When set, renders a pager. */
  pagination?: UserPanelPagination;
  /** Dim the list + show a spinner while a fetch is in flight. */
  loading?: boolean;
}

export function UserSearchPanel({
  users,
  selectedId,
  multiSelect,
  selectedIds,
  onSelect,
  rowAction,
  title = 'Users',
  totalCount,
  height,
  enableDeleted,
  menu,
  filterGroups = defaultUserFilterGroups,
  onFiltersChange,
  pagination,
  loading,
}: UserSearchPanelProps) {
  const [q, setQ] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // sel: { [groupKey]: string[] }
  const [sel, setSel] = useState<Record<string, string[]>>({});
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Report filter state upward so the parent can fetch the matching page.
  // SearchInput already debounces `q`, so this fires at most once per keystroke
  // burst. Reset-to-page-1 is the parent's responsibility.
  useEffect(() => {
    onFiltersChange?.({
      search: q,
      selections: sel,
      ageMin,
      ageMax,
      includeDeleted,
    });
  }, [q, sel, ageMin, ageMax, includeDeleted, onFiltersChange]);

  const toggle = (key: string, val: string) => {
    setSel((s) => {
      const cur = new Set(s[key] ?? []);
      cur.has(val) ? cur.delete(val) : cur.add(val);
      return { ...s, [key]: [...cur] };
    });
  };

  const clearAll = () => {
    setSel({});
    setAgeMin('');
    setAgeMax('');
  };

  const activeCount =
    filterGroups.reduce((n, g) => n + (sel[g.key]?.length ?? 0), 0) +
    (ageMin || ageMax ? 1 : 0);

  const deletedCount = users.filter((u) => u.deleted).length;
  const selectedSet = new Set(selectedIds ?? []);

  // The server already applied search + filters; render the rows as-is.
  const totalMatching = pagination?.total ?? totalCount;

  const CountPill = ({ n }: { n: number }) => (
    <span
      style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        background: 'var(--bg-muted)',
        borderRadius: 99,
        padding: '1px 7px',
      }}
    >
      {n}
    </span>
  );

  // ---- Pagination window math ----
  const pageStart = pagination && users.length > 0 ? pagination.offset + 1 : 0;
  const pageEnd = pagination ? pagination.offset + users.length : users.length;
  const canPrev = !!pagination && pagination.offset > 0;
  const canNext = !!pagination && pagination.hasMore;
  const goPrev = () => {
    if (!pagination) return;
    pagination.onOffsetChange(
      Math.max(0, pagination.offset - pagination.limit)
    );
  };
  const goNext = () => {
    if (!pagination) return;
    pagination.onOffsetChange(pagination.offset + pagination.limit);
  };

  return (
    <div className="panel" style={{ height, position: 'relative' }}>
      {/* ---- Header: title, filters button, search, toggles ---- */}
      <div
        style={{
          padding: 12,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {/* Top row: title + buttons */}
        <div
          className="row"
          style={{ justifyContent: 'space-between', marginBottom: 10 }}
        >
          <span className="panel-title">
            {title}{' '}
            <CountPill
              n={totalMatching != null ? totalMatching : users.length}
            />
          </span>
          <div className="row gap-1">
            <Button
              variant={activeCount > 0 ? 'subtle' : 'ghost'}
              size="sm"
              leftIcon="sliders-horizontal"
              onClick={() => setShowFilters(true)}
              title="Filters"
            >
              Filters
              {activeCount > 0 && (
                <span style={{ fontWeight: 700 }}>{activeCount}</span>
              )}
            </Button>
            {menu && menu.length > 0 && (
              <Dropdown
                align="right"
                width={224}
                trigger={
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    title="More actions"
                  >
                    <Icon name="more-vertical" size={17} />
                  </button>
                }
              >
                {menu.map((m, i) => (
                  <MenuItem
                    key={i}
                    icon={m.icon as any}
                    danger={m.danger}
                    onClick={m.onClick}
                  >
                    {m.label}
                  </MenuItem>
                ))}
              </Dropdown>
            )}
          </div>
        </div>

        {/* Search input */}
        <SearchInput
          value={q}
          onChange={setQ}
          debounce={250}
          placeholder="Search name, email…"
          size="sm"
        />

        {/* Include-deleted toggle */}
        {enableDeleted && (
          <label
            className="row"
            style={{
              justifyContent: 'space-between',
              marginTop: 10,
              cursor: 'pointer',
            }}
          >
            <span
              className="row gap-2"
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon
                name="trash"
                size={13}
                style={{ color: 'var(--text-muted)' }}
              />
              Include deleted
              {deletedCount > 0 && (
                <span
                  className="text-faint"
                  style={{ fontSize: 'var(--fs-2xs)' }}
                >
                  ({deletedCount})
                </span>
              )}
            </span>
            <Switch
              size="sm"
              checked={includeDeleted}
              onChange={setIncludeDeleted}
            />
          </label>
        )}

        {/* Active-filter count + clear */}
        {activeCount > 0 && (
          <div
            className="row"
            style={{
              justifyContent: 'space-between',
              marginTop: 9,
              fontSize: 'var(--fs-xs)',
            }}
          >
            <span className="text-muted">
              {activeCount}{' '}
              {activeCount === 1 ? 'filter active' : 'filters active'}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              style={{
                height: 22,
                padding: '0 7px',
                color: 'var(--accent-text)',
              }}
              onClick={clearAll}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ---- User list ---- */}
      <div
        className="grow scroll-y"
        style={{ padding: 6, position: 'relative' }}
      >
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-canvas)',
              opacity: 0.6,
            }}
          >
            <Spinner />
          </div>
        )}
        {users.length === 0 && !loading ? (
          <EmptyState
            icon="search"
            title="No users found"
            desc="Try a different name or clear filters."
          />
        ) : (
          users.map((u) => {
            const isSelected = multiSelect
              ? selectedSet.has(u.id)
              : u.id === selectedId;
            return (
              <UserRow
                key={u.id}
                user={u}
                selected={isSelected}
                onClick={() => onSelect(u.id)}
                action={
                  multiSelect
                    ? (usr) => (
                        // Row click already toggles; stop propagation so a click
                        // on the checkbox itself doesn't fire the handler twice.
                        <span
                          onClick={(e) => e.stopPropagation()}
                          style={{ display: 'flex' }}
                        >
                          <Checkbox
                            checked={selectedSet.has(usr.id)}
                            onChange={() => onSelect(usr.id)}
                          />
                        </span>
                      )
                    : rowAction
                }
              />
            );
          })
        )}
      </div>

      {/* ---- Pagination footer ---- */}
      {pagination && (canPrev || canNext || users.length > 0) && (
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderTop: '1px solid var(--border)',
            flexShrink: 0,
            fontSize: 'var(--fs-xs)',
          }}
        >
          <span className="text-muted">
            {pageStart}–{pageEnd}
            {totalMatching != null
              ? ` of ${totalMatching.toLocaleString()}`
              : ''}
          </span>
          <div className="row gap-1">
            <Button
              variant="ghost"
              size="sm"
              leftIcon="chevron-left"
              disabled={!canPrev || loading}
              onClick={goPrev}
            >
              Prev
            </Button>
            <Button
              variant="ghost"
              size="sm"
              rightIcon="chevron-right"
              disabled={!canNext || loading}
              onClick={goNext}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ---- Filters overlay ---- */}
      {showFilters && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 30,
            background: 'var(--bg-canvas)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 'inherit',
            animation: 'modal-in .14s ease',
          }}
        >
          {/* Overlay header */}
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              className="row gap-2"
              style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}
            >
              <Icon
                name="sliders-horizontal"
                size={16}
                style={{ color: 'var(--text-muted)' }}
              />
              Filters
              {activeCount > 0 && <CountPill n={activeCount} />}
            </span>
            <div className="row gap-1">
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--text-muted)' }}
                onClick={clearAll}
                disabled={activeCount === 0}
              >
                Clear all
              </button>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                title="Close"
                onClick={() => setShowFilters(false)}
              >
                <Icon name="x" size={16} />
              </Button>
            </div>
          </div>

          {/* Overlay body: filter groups + age range */}
          <div className="grow scroll-y" style={{ padding: '2px 14px 16px' }}>
            {filterGroups.map((g) => (
              <div key={g.key} style={{ marginTop: 16 }}>
                <div className="eyebrow" style={{ marginBottom: 9 }}>
                  {g.label}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {g.options.map(([val, label]) => (
                    <FilterChip
                      key={val}
                      active={(sel[g.key] ?? []).includes(val)}
                      onClick={() => toggle(g.key, val)}
                    >
                      {label}
                    </FilterChip>
                  ))}
                </div>
              </div>
            ))}

            {/* Age range */}
            <div style={{ marginTop: 18 }}>
              <div className="eyebrow" style={{ marginBottom: 9 }}>
                Age range
              </div>
              <div className="row gap-2" style={{ alignItems: 'center' }}>
                <Input
                  size="sm"
                  type="number"
                  min={18}
                  max={99}
                  placeholder="Min"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <span
                  className="text-muted"
                  style={{ fontSize: 'var(--fs-sm)' }}
                >
                  to
                </span>
                <Input
                  size="sm"
                  type="number"
                  min={18}
                  max={99}
                  placeholder="Max"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>
          </div>

          {/* Overlay footer: show-results button */}
          <div
            style={{
              padding: 12,
              borderTop: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            <Button
              variant="primary"
              className="btn-block"
              onClick={() => setShowFilters(false)}
            >
              {totalMatching != null
                ? `Show ${totalMatching.toLocaleString()} ${
                    totalMatching === 1 ? 'result' : 'results'
                  }`
                : 'Show results'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserSearchPanel;
